import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter } from 'lucide-react';
import { enhancedProducts } from '@/data/enhancedProducts';
import ProductCarousel from './ProductCarousel';
import ProductQuickView from './ProductQuickView';
import { Product } from '@/types/product';
import { Button } from '@/components/ui/button';

const ProductSections: React.FC = () => {
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const mensProducts = enhancedProducts.filter(p => p.category === 'mens');
  const womensProducts = enhancedProducts.filter(p => p.category === 'womens');  
  const accessoriesProducts = enhancedProducts.filter(p => p.category === 'accessories');

  const handleQuickView = (product: Product) => {
    setQuickViewProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleCloseQuickView = () => {
    setIsQuickViewOpen(false);
    setQuickViewProduct(null);
  };

  const handleAddToCart = (product: Product, variant: any, quantity: number = 1) => {
    console.log('Adding to cart:', { product: product.name, variant, quantity });
    // TODO: Implement actual cart functionality
  };



  const getCategoryStats = (products: any[]) => {
    const totalProducts = products.length;
    const prices = products.map(p => p.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    return { totalProducts, minPrice, maxPrice };
  };

  const SectionHeader = ({ 
    title, 
    stats, 
    gradient,
    index 
  }: { 
    title: string; 
    stats: ReturnType<typeof getCategoryStats>;
    gradient: string;
    index: number;
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="relative mb-6 md:mb-8"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 px-4">
        <div>
          <h2 className={`text-3xl md:text-5xl lg:text-6xl font-black ${gradient} bg-clip-text text-transparent mb-2`}>
            {title}
          </h2>
          <div className="flex flex-wrap items-center gap-3 text-white/80">
            <span className="text-sm md:text-base font-medium">
              {stats.totalProducts} Products
            </span>
            <span className="w-1 h-1 bg-lime-400 rounded-full hidden sm:block"></span>
            <span className="text-sm md:text-base font-medium">
              ${stats.minPrice} - ${stats.maxPrice}
            </span>
          </div>
        </div>
        
        {/* Mobile-friendly stats */}
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 border border-lime-400/20">
            <div className="text-lime-400 text-xs uppercase tracking-wide font-bold">New Arrivals</div>
            <div className="text-white text-sm font-semibold">
              {enhancedProducts.filter(p => p.badge === 'NEW' && p.category === title.toLowerCase().replace("'s", "s")).length}
            </div>
          </div>
          <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 border border-lime-400/20">
            <div className="text-lime-400 text-xs uppercase tracking-wide font-bold">Bestsellers</div>
            <div className="text-white text-sm font-semibold">
              {enhancedProducts.filter(p => p.badge === 'BESTSELLER' && p.category === title.toLowerCase().replace("'s", "s")).length}
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative element */}
      <div className="absolute -bottom-2 left-4 w-16 md:w-24 h-1 bg-gradient-to-r from-lime-400 to-transparent rounded-full"></div>
    </motion.div>
  );

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-lime-400/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-lime-400/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto space-y-16 md:space-y-24">
        
        {/* Products Header with Search Link */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-8 md:mb-12 px-4"
        >
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black bg-gradient-to-r from-lime-400 via-lime-300 to-lime-400 bg-clip-text text-transparent mb-4">
            Our Collection
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-6">
            Premium pickleball gear designed for every athlete. Discover adaptive-first apparel and accessories.
          </p>
          
          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link to="/products">
              <Button className="bg-lime-400 hover:bg-lime-500 text-black font-bold px-6 py-3 rounded-xl text-base transition-all duration-300 hover:scale-105">
                <Search className="w-4 h-4 mr-2" />
                Search All Products
              </Button>
            </Link>
            <div className="flex items-center gap-4 text-white/60 text-sm">
              <span>{enhancedProducts.length} total products</span>
              <span className="w-1 h-1 bg-lime-400 rounded-full"></span>
              <span>Free shipping $75+</span>
            </div>
          </div>
        </motion.div>
        
        {/* Men's Section */}
        <div>
          <SectionHeader
            title="Men's"
            stats={getCategoryStats(mensProducts)}
            gradient="bg-gradient-to-r from-lime-400 via-lime-300 to-lime-400"
            index={0}
          />
          <ProductCarousel
            products={mensProducts}
            onQuickView={handleQuickView}
            onAddToCart={handleAddToCart}
          />
        </div>

        {/* Women's Section */}
        <div>
          <SectionHeader
            title="Women's"
            stats={getCategoryStats(womensProducts)}
            gradient="bg-gradient-to-r from-pink-400 via-lime-400 to-pink-400"
            index={1}
          />
          <ProductCarousel
            products={womensProducts}
            onQuickView={handleQuickView}
            onAddToCart={handleAddToCart}
          />
        </div>

        {/* Accessories Section */}
        <div>
          <SectionHeader
            title="Accessories"
            stats={getCategoryStats(accessoriesProducts)}
            gradient="bg-gradient-to-r from-orange-400 via-lime-400 to-orange-400"
            index={2}
          />
          <ProductCarousel
            products={accessoriesProducts}
            onQuickView={handleQuickView}
            onAddToCart={handleAddToCart}
          />
        </div>

      </div>

      {/* Call to Action Section - Mobile Optimized */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="relative mt-16 md:mt-24 mx-4 md:mx-8"
      >
        <div className="bg-gradient-to-r from-lime-400/20 via-lime-400/10 to-lime-400/20 border border-lime-400/30 rounded-2xl p-6 md:p-8 text-center backdrop-blur-sm">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-white mb-3 md:mb-4">
              Ready to Dominate the Court?
            </h3>
            <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8 leading-relaxed">
              Join thousands of players elevating their game with premium PBX gear. 
              From performance apparel to pro accessories.
            </p>
            
            {/* Mobile-optimized action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button className="w-full sm:w-auto bg-lime-400 hover:bg-lime-500 text-black font-bold py-4 px-8 rounded-xl text-base md:text-lg transition-all duration-300 hover:shadow-xl hover:scale-105">
                SHOP ALL COLLECTIONS
              </button>
              <button className="w-full sm:w-auto border-2 border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black font-bold py-4 px-8 rounded-xl text-base md:text-lg transition-all duration-300">
                VIEW SIZE GUIDE
              </button>
            </div>

            {/* Trust indicators - mobile friendly */}
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 mt-6 md:mt-8 text-white/60 text-xs md:text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
                <span>Free shipping $75+</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
                <span>30-day returns</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
                <span>Premium materials</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick View Modal */}
      <ProductQuickView
        product={quickViewProduct}
        isOpen={isQuickViewOpen}
        onClose={handleCloseQuickView}
        onAddToCart={handleAddToCart}
      />
    </section>
  );
};

export default ProductSections; 